<template>
  <b-modal id="olympic-diffs-modal" size="xl"
           centered
  >
    <template #modal-title>
      <h5>
        <em class="fas fa-edit"></em> Сравнение введенных данных с данными из системы олимпиад
      </h5>
    </template>
    <template #default>
      <div :hidden="!rowData || !rowData.length || !loaded">
        <div>{{ textForOlympicDiffsMain }}</div>
        <div>
          <ag-grid-vue
            class="ag-theme-material ag-grid-diff-olymp-stat"
            :columnDefs="columnDefs"
            :gridOptions="gridOptions"
            :rowData="rowData"
            @grid-ready="onGridReady"
            @gridSizeChanged="onGridSizeChanged"
            @column-resized="onColumnResized">
          </ag-grid-vue>
        </div>
      </div>
      <div v-if="(!rowData || !rowData.length) && loaded">
        {{ textForOlympicDiffsEmpty }}
      </div>
      <div v-else-if="!loaded">
        <div>{{ textForOlympicDiffsLoading }}</div>
        <div class="d-flex justify-content-center">
          <b-spinner
            variant="primary"
            class="spinner-olymp-diff"
            label="Загрузка данных..."/>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <b-button cols="auto" variant="primary" @click="closeModal">
        Закрыть
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue'
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'

import AG_GRID_LOCALE_RU from '@/config/locale.ru'
import statsApi from '@/api/statistics'

export default {
  name: 'OlympicDiffsModal',
  components: {
    'ag-grid-vue': AgGridVue
  },
  props: {
    olympTitle: {
      type: String,
      default() {
        return null
      }
    }
  },
  data() {
    return {
      textForOlympicDiffsMain: 'В данной таблице представлены различия между данными заполненными '
        + 'через данную систему и систему Газпром-олимпиад. Поля, в которых найдены'
        + ' несовпадения, выделены цветом. Формат: данные из системы (данные из Газпром-олимпиад).',
      textForOlympicDiffsEmpty: 'Различия между данными заполненными через данную систему и '
        + 'систему Газпром-олимпиад отсутствуют.',
      textForOlympicDiffsLoading: 'Выполняется загрузка данных из системы Газпром-олимпиад...',
      loaded: false,
      currentOlympTitle: null,
      gridOptions: {
        defaultColDef: {
          autoHeight: true,
          lockPosition: true,
          headerClass: 'table-olymp-stats-header',
          sortable: true,
          suppressSizeToFit: true,
          width: 180
        },
        localeText: AG_GRID_LOCALE_RU,
        headerHeight: 80,
        enableCellTextSelection: true,
        suppressMenuHide: true
      },
      columnDefs: [
        {
          headerName: 'Класс',
          field: 'title',
          suppressSizeToFit: false,
          width: 170,
          minWidth: 170
        },
        {
          headerName: 'Зарегистрировано в олимпиаде',
          field: 'registered',
          cellClass: this.getCellClass
        },
        {
          headerName: 'Участники заключительного этапа',
          field: 'membersOlymp',
          cellClass: this.getCellClass
        },
        {
          headerName: 'Призёры',
          field: 'semiwinners',
          width: 115,
          cellClass: this.getCellClass
        },
        {
          headerName: 'Победители',
          field: 'winners',
          width: 140,
          cellClass: this.getCellClass
        }
      ],
      rowData: null
    }
  },
  computed: {
    ...mapGetters({
      statsOlympicsResults: 'statistics/getStatsOlympicsResults',
      orgs: 'dicts/getOrganizations'
    })
  },
  watch: {
    statsOlympicsResults(value) {
      this.getDataDiffs(value)
    }
  },
  methods: {
    ...mapActions({
      pushToasts: 'toasts/pushToasts'
    }),
    onColumnResized() {
      this.gridApi.resetRowHeights()
    },
    getDataDiffs(currentData) {
      if (this.gridApi && currentData && this.olympTitle) {
        const newData = _.cloneDeep(currentData)
        newData.forEach((td) => {
          td.title = this.orgs.find((p) => p.id
            === td.organizationId).title
        })
        try {
          this.loaded = false
          statsApi.getStatisticsFromOlympiadApi().then((response) => {
            const dataFromApi = response.data[this.olympTitle]
            if (dataFromApi) {
              dataFromApi.forEach((td) => {
                td.title = this.orgs.find((p) => p.id
                  === td.organizationId).title
              })
              this.currentOlympTitle = this.olympTitle
              const diffData = []
              for (const orgData of newData) {
                const apiOrgData = dataFromApi
                  .find((td) => td.organizationId === orgData.organizationId)
                const keysForChanges = {
                  registered: orgData.registered,
                  membersOlymp: orgData.membersOlymp,
                  semiwinners: orgData.semiwinners,
                  winners: orgData.winners
                }
                if (apiOrgData) {
                  const diff = _.omitBy(keysForChanges,
                    (value, key) => _.isEqual(Number(apiOrgData[key]),
                      Number(value)))
                  if (!_.isEmpty(diff)) {
                    diffData.push(orgData)
                    for (const key of Object.keys(diff)) {
                      diffData[diffData.length - 1][key] = `${diffData[diffData.length - 1][key]} (${apiOrgData[key]})`
                    }
                  }
                }
              }
              this.rowData = diffData
              this.loaded = true
            } else {
              this.pushToasts({
                title: 'Уведомление',
                message: 'Для данной олимпиады нет данных из системы Газпром-олимпиад.'
              })
              this.closeModal()
            }
          }).catch(() => {
            this.pushToasts({
              title: 'Уведомление',
              message: 'Для данной олимпиады нет данных из системы Газпром-олимпиад.'
            })
            this.closeModal()
          })
        } catch {
          this.pushToasts({
            error: true,
            title: 'Уведомление',
            message: 'При отправке запроса к системе Газпром-олимпиад произошла ошибка.'
          })
          this.closeModal()
        }
      }
    },
    getCellClass(params) {
      if (_.isString(params.value) && params.value.split('(').length > 1) {
        return 'bg-warning'
      }
      return ''
    },
    closeModal() {
      this.$bvModal.hide('olympic-diffs-modal')
    },
    onGridReady(params) {
      this.gridApi = params.api
      this.gridApi.sizeColumnsToFit()
      this.getDataDiffs(this.statsOlympicsResults)
    },
    onGridSizeChanged() {
      this.gridApi.sizeColumnsToFit()
    }
  }
}
</script>


<style>
  .ag-grid-diff-olymp-stat {
    height: 500px;
    border-bottom: 1px solid #017ac3;
    margin-left: 20px;
  }
  .ag-grid-diff-olymp-stat .ag-header-cell-label .ag-header-cell-text {
    height: auto;
    white-space: normal;
    word-wrap: break-word;
    text-align: center;
  }
  .ag-grid-diff-olymp-stat .ag-icon {
    color: #017ac3;
  }
  .spinner-olymp-diff {
    height: 3rem;
    width: 3rem;
  }
</style>
